import React, { useEffect, useState } from "react";
import { Bell, BellOff, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { useAuth } from "../lib/auth";
import {
  isPushSupported, pushPermission, subscribeToPush, unsubscribeFromPush,
  pushSubscribedLocal, pushPromptDismissed, dismissPushPrompt,
} from "../lib/push";

const ELIGIBLE = ["admin", "contracts_manager", "supervisor"];

/**
 * Renders a slim banner asking on-call eligible roles to enable push notifications,
 * UNLESS the user has already subscribed or has dismissed the prompt this session.
 * Mounted once inside AppShell.
 */
export default function PushPermissionPrompt() {
  const { user } = useAuth();
  const [permission, setPermission] = useState(() => (typeof window !== "undefined" ? pushPermission() : "default"));
  const [subscribed, setSubscribed] = useState(pushSubscribedLocal());
  const [dismissed, setDismissed] = useState(pushPromptDismissed());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { setPermission(pushPermission()); }, []);

  if (!user || !ELIGIBLE.includes(user.role)) return null;
  if (!isPushSupported()) return null;
  if (subscribed || dismissed) return null;
  if (permission === "denied") return null;

  const enable = async () => {
    setBusy(true); setError("");
    try {
      await subscribeToPush();
      setSubscribed(true);
      setPermission("granted");
    } catch (e) { setError(e.message || "Failed to enable"); }
    finally { setBusy(false); }
  };

  const skip = () => { dismissPushPrompt(); setDismissed(true); };

  return (
    <div data-testid="push-prompt-banner" style={{ background: "#0f172a", color: "#fff", padding: "10px 16px", display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", borderBottom: "2px solid #ff5a00" }}>
      <Bell size={18} color="#ff5a00" />
      <div style={{ flex: 1, minWidth: 220 }}>
        <div className="mono" style={{ fontSize: 10, letterSpacing: "0.12em", color: "#94a3b8" }}>NRS · PUSH NOTIFICATIONS</div>
        <div style={{ fontWeight: 700, fontSize: 13 }}>Enable callout push to be alerted on this device when you&apos;re on call.</div>
        {error ? <div style={{ marginTop: 4, fontSize: 11, color: "#fca5a5" }}><AlertTriangle size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />{error}</div> : null}
      </div>
      <button data-testid="push-enable-btn" onClick={enable} disabled={busy} className="btn-primary" style={{ background: "#ff5a00", color: "#fff", fontWeight: 700 }}>
        {busy ? "Enabling…" : "Enable notifications"}
      </button>
      <button data-testid="push-skip-btn" onClick={skip} className="btn-ghost" style={{ color: "#cbd5e1", border: "1px solid #334155" }}>
        <X size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />Not now
      </button>
    </div>
  );
}

/** Profile-page or settings-page toggle — always-available recoverable control. */
export function PushToggle() {
  const { user } = useAuth();
  const [permission, setPermission] = useState(() => pushPermission());
  const [subscribed, setSubscribed] = useState(pushSubscribedLocal());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [serverStatus, setServerStatus] = useState(null);
  const eligible = user && ELIGIBLE.includes(user.role);
  const canSeeStatus = user && ["admin", "contracts_manager"].includes(user.role);

  React.useEffect(() => {
    if (!canSeeStatus) return;
    (async () => {
      try {
        const { default: api } = await import("../lib/api");
        const { data } = await api.get("/admin/push/status");
        setServerStatus(data);
      } catch { /* noop */ }
    })();
  }, [canSeeStatus]);

  if (!eligible) return null;
  if (!isPushSupported()) {
    return (
      <div data-testid="push-toggle-unsupported" style={{ padding: 12, background: "#fef3c7", color: "#92400e", borderLeft: "4px solid #f59e0b", borderRadius: 4, fontSize: 12 }}>
        <AlertTriangle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
        Push notifications are not supported on this browser. Use Chrome (desktop/Android) or Safari 16.4+ (iOS/macOS).
      </div>
    );
  }

  const toggle = async () => {
    setBusy(true); setError(""); setInfo("");
    try {
      if (subscribed) {
        await unsubscribeFromPush();
        setSubscribed(false);
      } else {
        await subscribeToPush();
        setSubscribed(true);
        setPermission("granted");
      }
    } catch (e) { setError(e.message || "Failed"); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true); setError(""); setInfo("");
    try {
      const { default: api } = await import("../lib/api");
      const { data } = await api.post("/admin/push/test");
      if (data.sent > 0) setInfo(`Test push fired to ${data.sent} device(s). Check the notification tray.`);
      else if (data.reason === "vapid_not_configured") setError("Server can't dispatch: VAPID private key is not configured on the backend. See server status below.");
      else if (data.total_subscriptions === 0) setError("No active subscriptions on file for this user. Enable push first, then try the test.");
      else setError(`Push dispatch returned 0 sent / ${data.failed || 0} failed. Likely a stale endpoint — disable and re-enable.`);
    } catch (e) { setError(e.message || "Test failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="push-toggle" className="card" style={{ padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {subscribed ? <Bell size={20} color="#059669" /> : <BellOff size={20} color="#64748b" />}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700 }}>Callout push notifications</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>{subscribed ? "Enabled on this device — you will receive a push when a callout fires." : permission === "denied" ? "Blocked by browser — re-enable from your browser site settings." : "Not enabled on this device."}</div>
          {error ? <div style={{ marginTop: 4, fontSize: 11, color: "#b91c1c" }}><AlertTriangle size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />{error}</div> : null}
          {info ? <div style={{ marginTop: 4, fontSize: 11, color: "#065f46" }}>{info}</div> : null}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {subscribed ? (
            <button data-testid="push-test-btn" onClick={sendTest} disabled={busy} className="btn-ghost" style={{ fontSize: 12 }}>Send test</button>
          ) : null}
          <button data-testid="push-toggle-btn" onClick={toggle} disabled={busy || permission === "denied"} className={subscribed ? "btn-ghost" : "btn-primary"}>
            {busy ? "…" : subscribed ? "Disable" : "Enable"}
          </button>
        </div>
      </div>
      {canSeeStatus && serverStatus ? (
        <div data-testid="push-server-status" className="mono" style={{ marginTop: 10, padding: 8, background: serverStatus.vapid_configured ? "#ecfdf5" : "#fef3c7", borderLeft: `3px solid ${serverStatus.vapid_configured ? "#059669" : "#f59e0b"}`, fontSize: 11, color: serverStatus.vapid_configured ? "#065f46" : "#92400e" }}>
          <strong>SERVER STATUS · </strong>
          {serverStatus.vapid_configured
            ? `READY · ${serverStatus.active_subscriptions_total} active subscription(s) site-wide`
            : "PUSH DISPATCH OFFLINE — backend cannot send pushes. See warnings below."}
          {(serverStatus.warnings || []).map((w, i) => (
            <div key={i} style={{ marginTop: 4 }}>· {w}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
